import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, LogOut, User, CreditCard, Tag, Menu } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface TopbarProps {
  /** Opens the mobile sidebar drawer (hamburger, `lg:hidden`). */
  onMenuClick?: () => void;
  /**
   * Opens the unified SettingsModal (owned by DashboardLayout). Settings is no
   * longer a route, so the notifications bell and the profile menu item open the
   * modal in place instead of navigating to the retired /dashboard/settings
   * route (which redirects to /agent — a dead end from a product page).
   */
  onOpenSettings?: () => void;
}

/**
 * Route → i18n key for the breadcrumb. The product routes intentionally resolve
 * to proper-noun labels that read identically in every locale, so the
 * breadcrumb never disagrees with the sidebar; only the generic chrome (Home,
 * Billing, Settings, the fallback) is actually translated.
 */
const breadcrumbKeyMap: Record<string, string> = {
  '/dashboard': 'home',
  '/dashboard/kol': 'kol',
  '/dashboard/medical-watch': 'medicalWatch',
  '/dashboard/personas': 'personas',
  '/dashboard/market-access': 'marketAccess',
  '/dashboard/pharmageo': 'pharmageo',
  '/dashboard/billing': 'billing',
  '/dashboard/settings': 'settings',
};

export function Topbar({ onMenuClick, onOpenSettings }: TopbarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation('dashboard');

  const breadcrumbKey = breadcrumbKeyMap[location.pathname];
  const currentPage = breadcrumbKey ? t(`shell.nav.${breadcrumbKey}`) : t('shell.nav.fallback');
  const userName = user?.name || t('shell.userFallback');
  const initials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  return (
    <header className="sticky top-0 z-30 flex h-12 items-center justify-between border-b border-border bg-card px-4 sm:px-6">
      {/* Left: hamburger (mobile only) + breadcrumb — simple text, no duplicate logo */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 lg:hidden"
          aria-label={t('shell.openMenu')}
          onClick={onMenuClick}
        >
          <Menu className="h-4 w-4 text-muted-foreground" />
        </Button>
        <span className="text-sm font-medium text-foreground">{currentPage}</span>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('shell.notifications')} onClick={() => onOpenSettings?.()}>
          <Bell className="h-4 w-4 text-muted-foreground" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-7 w-7 rounded-full p-0" aria-label={t('shell.userMenu')}>
              <Avatar className="h-7 w-7">
                <AvatarFallback className="bg-secondary text-muted-foreground text-[10px] font-semibold">{initials}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-52" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{userName}</p>
                <p className="text-xs text-muted-foreground">{user?.email || ''}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onOpenSettings?.()}>
              <User className="mr-2 h-3.5 w-3.5" /> {t('shell.menu.profile')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/dashboard/billing')}>
              <CreditCard className="mr-2 h-3.5 w-3.5" /> {t('shell.menu.billing')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/agent?topup=1')}>
              <Tag className="mr-2 h-3.5 w-3.5" /> {t('shell.menu.pricing')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>
              <LogOut className="mr-2 h-3.5 w-3.5" /> {t('shell.menu.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
